import { useEffect, useState, useMemo } from 'react'
import { useCookies } from 'react-cookie'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from './featureFlags'

interface LocationData {
	country_code: string
	country_name: string
	currency: string
	// Add other fields from the API response if needed
}

interface PricingData {
	monthly: string
	yearly: string
	yearlyTotal: string
	discount: string
	currencySymbol: string
	flagEmoji: string
	stripePriceId: {
		yearly: string
		monthly: string
	}
}

const MONTHLY_STRIPE_PRICE_ID = 'price_1RgP4EApflHFLqu6KCPmApu7'
const YEARLY_STRIPE_PRICE_ID = 'price_1RgdrjApflHFLqu6sQapHvyo'

const NEW_MONTHLY_STRIPE_PRICE_ID = 'price_1TEkFQApflHFLqu6y5MtqLXb'
const NEW_YEARLY_STRIPE_PRICE_ID = 'price_1TEkLYApflHFLqu6HTr7tHJi'

const LOCATION_COOKIE_NAME = 'userLocation'
const LOCATION_COOKIE_EXPIRY_DAYS = 30

// New pricing tiers (2024 updated pricing)
const NEW_COUNTRY_PRICING: Record<string, PricingData> = {
	// United States
	US: {
		monthly: '35.99',
		yearly: '16.58',
		yearlyTotal: '199.00',
		discount: '50',
		currencySymbol: '$',
		flagEmoji: '🇺🇸',
		stripePriceId: {
			yearly: NEW_YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Eurozone countries
	// France
	FR: {
		monthly: '29.99',
		yearly: '14.08',
		yearlyTotal: '169',
		discount: '55',
		currencySymbol: '€',
		flagEmoji: '🇫🇷',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Germany
	DE: {
		monthly: '29.99',
		yearly: '14.08',
		yearlyTotal: '169',
		discount: '55',
		currencySymbol: '€',
		flagEmoji: '🇩🇪',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Italy
	IT: {
		monthly: '29.99',
		yearly: '14.08',
		yearlyTotal: '169',
		discount: '55',
		currencySymbol: '€',
		flagEmoji: '🇮🇹',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Spain
	ES: {
		monthly: '29.99',
		yearly: '14.08',
		yearlyTotal: '169',
		discount: '55',
		currencySymbol: '€',
		flagEmoji: '🇪🇸',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Netherlands
	NL: {
		monthly: '29.99',
		yearly: '14.08',
		yearlyTotal: '169',
		discount: '55',
		currencySymbol: '€',
		flagEmoji: '🇳🇱',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Portugal
	PT: {
		monthly: '29.99',
		yearly: '14.08',
		yearlyTotal: '169',
		discount: '55',
		currencySymbol: '€',
		flagEmoji: '🇵🇹',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// United Kingdom
	GB: {
		monthly: '27.99',
		yearly: '12.42',
		yearlyTotal: '149',
		discount: '55',
		currencySymbol: '£',
		flagEmoji: '🇬🇧',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Canada
	CA: {
		monthly: '44.99',
		yearly: '20.75',
		yearlyTotal: '249',
		discount: '55',
		currencySymbol: 'C$',
		flagEmoji: '🇨🇦',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Australia
	AU: {
		monthly: '49.99',
		yearly: '23.25',
		yearlyTotal: '279',
		discount: '55',
		currencySymbol: 'A$',
		flagEmoji: '🇦🇺',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// New Zealand
	NZ: {
		monthly: '23.50',
		yearly: '8.67',
		yearlyTotal: '104',
		discount: '60',
		currencySymbol: 'NZ$',
		flagEmoji: '🇳🇿',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Singapore
	SG: {
		monthly: '20',
		yearly: '6.25',
		yearlyTotal: '75',
		discount: '70',
		currencySymbol: 'S$',
		flagEmoji: '🇸🇬',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Switzerland
	CH: {
		monthly: '18',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '70',
		currencySymbol: 'CHF',
		flagEmoji: '🇨🇭',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Japan
	JP: {
		monthly: '3999',
		yearly: '1825',
		yearlyTotal: '21900',
		discount: '55',
		currencySymbol: '¥',
		flagEmoji: '🇯🇵',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// South Korea
	KR: {
		monthly: '39900',
		yearly: '18250',
		yearlyTotal: '219000',
		discount: '55',
		currencySymbol: '₩',
		flagEmoji: '🇰🇷',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// China
	CN: {
		monthly: '149',
		yearly: '66.58',
		yearlyTotal: '799',
		discount: '55',
		currencySymbol: '¥',
		flagEmoji: '🇨🇳',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// India
	IN: {
		monthly: '999',
		yearly: '458.25',
		yearlyTotal: '5499',
		discount: '55',
		currencySymbol: '₹',
		flagEmoji: '🇮🇳',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Philippines
	PH: {
		monthly: '999',
		yearly: '458.25',
		yearlyTotal: '5499',
		discount: '55',
		currencySymbol: '₱',
		flagEmoji: '🇵🇭',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Mexico
	MX: {
		monthly: '349',
		yearly: '158.25',
		yearlyTotal: '1899',
		discount: '55',
		currencySymbol: 'MX$',
		flagEmoji: '🇲🇽',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Brazil
	BR: {
		monthly: '99.90',
		yearly: '45.75',
		yearlyTotal: '549',
		discount: '55',
		currencySymbol: 'R$',
		flagEmoji: '🇧🇷',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Russia
	RU: {
		monthly: '1499',
		yearly: '666.58',
		yearlyTotal: '7999',
		discount: '55',
		currencySymbol: '₽',
		flagEmoji: '🇷🇺',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: NEW_MONTHLY_STRIPE_PRICE_ID,
		},
	},
}

// Legacy pricing (current pricing)
const COUNTRY_PRICING: Record<string, PricingData> = {
	// United States
	US: {
		monthly: '9.00',
		yearly: '6.00',
		yearlyTotal: '72.00',
		discount: '33',
		currencySymbol: '$',
		flagEmoji: '🇺🇸',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Eurozone countries
	FR: {
		monthly: '8.50',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '33',
		currencySymbol: '€',
		flagEmoji: '🇫🇷',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	DE: {
		monthly: '8.50',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '33',
		currencySymbol: '€',
		flagEmoji: '🇩🇪',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	IT: {
		monthly: '8.50',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '33',
		currencySymbol: '€',
		flagEmoji: '🇮🇹',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	ES: {
		monthly: '8.50',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '33',
		currencySymbol: '€',
		flagEmoji: '🇪🇸',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	NL: {
		monthly: '8.50',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '33',
		currencySymbol: '€',
		flagEmoji: '🇳🇱',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	PT: {
		monthly: '8.50',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '33',
		currencySymbol: '€',
		flagEmoji: '🇵🇹',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// United Kingdom
	GB: {
		monthly: '7.50',
		yearly: '5.00',
		yearlyTotal: '60',
		discount: '33',
		currencySymbol: '£',
		flagEmoji: '🇬🇧',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Canada
	CA: {
		monthly: '11.50',
		yearly: '7.67',
		yearlyTotal: '92',
		discount: '33',
		currencySymbol: 'C$',
		flagEmoji: '🇨🇦',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Australia
	AU: {
		monthly: '12.00',
		yearly: '8.00',
		yearlyTotal: '96',
		discount: '33',
		currencySymbol: 'A$',
		flagEmoji: '🇦🇺',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// New Zealand
	NZ: {
		monthly: '13.00',
		yearly: '8.67',
		yearlyTotal: '104',
		discount: '33',
		currencySymbol: 'NZ$',
		flagEmoji: '🇳🇿',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Japan
	JP: {
		monthly: '950',
		yearly: '608',
		yearlyTotal: '7300',
		discount: '36',
		currencySymbol: '¥',
		flagEmoji: '🇯🇵',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// South Korea
	KR: {
		monthly: '10500',
		yearly: '6833',
		yearlyTotal: '82000',
		discount: '35',
		currencySymbol: '₩',
		flagEmoji: '🇰🇷',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Singapore
	SG: {
		monthly: '9.50',
		yearly: '6.25',
		yearlyTotal: '75',
		discount: '34',
		currencySymbol: 'S$',
		flagEmoji: '🇸🇬',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Hong Kong
	HK: {
		monthly: '70.00',
		yearly: '47.00',
		yearlyTotal: '560',
		discount: '33',
		currencySymbol: 'HK$',
		flagEmoji: '🇭🇰',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Norway
	NO: {
		monthly: '90.00',
		yearly: '60.00',
		yearlyTotal: '720',
		discount: '33',
		currencySymbol: 'kr',
		flagEmoji: '🇳🇴',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Sweden
	SE: {
		monthly: '95.00',
		yearly: '63.00',
		yearlyTotal: '760',
		discount: '34',
		currencySymbol: 'kr',
		flagEmoji: '🇸🇪',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Switzerland
	CH: {
		monthly: '8.50',
		yearly: '5.67',
		yearlyTotal: '68',
		discount: '33',
		currencySymbol: 'CHF',
		flagEmoji: '🇨🇭',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// China
	CN: {
		monthly: '42.00',
		yearly: '28.00',
		yearlyTotal: '340',
		discount: '33',
		currencySymbol: '¥',
		flagEmoji: '🇨🇳',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// India
	IN: {
		monthly: '300',
		yearly: '200',
		yearlyTotal: '2400',
		discount: '33',
		currencySymbol: '₹',
		flagEmoji: '🇮🇳',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Brazil
	BR: {
		monthly: '20.00',
		yearly: '13.00',
		yearlyTotal: '160',
		discount: '35',
		currencySymbol: 'R$',
		flagEmoji: '🇧🇷',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Mexico
	MX: {
		monthly: '80.00',
		yearly: '53',
		yearlyTotal: '640',
		discount: '34',
		currencySymbol: 'MX$',
		flagEmoji: '🇲🇽',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Russia
	RU: {
		monthly: '300',
		yearly: '200',
		yearlyTotal: '2400',
		discount: '33',
		currencySymbol: '₽',
		flagEmoji: '🇷🇺',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Turkey
	TR: {
		monthly: '90',
		yearly: '60',
		yearlyTotal: '720',
		discount: '33',
		currencySymbol: '₺',
		flagEmoji: '🇹🇷',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// South Africa
	ZA: {
		monthly: '85',
		yearly: '53',
		yearlyTotal: '640',
		discount: '38',
		currencySymbol: 'R',
		flagEmoji: '🇿🇦',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
	// Philippines
	PH: {
		monthly: '235',
		yearly: '158',
		yearlyTotal: '1900',
		discount: '33',
		currencySymbol: '₱',
		flagEmoji: '🇵🇭',
		stripePriceId: {
			yearly: YEARLY_STRIPE_PRICE_ID,
			monthly: MONTHLY_STRIPE_PRICE_ID,
		},
	},
}

// Default pricing (US) for fallback
const DEFAULT_PRICING = COUNTRY_PRICING.US
const NEW_DEFAULT_PRICING = NEW_COUNTRY_PRICING.US

export const useLocationPricing = () => {
	const [cookies, setCookie] = useCookies([LOCATION_COOKIE_NAME])
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [locationData, setLocationData] = useState<LocationData | null>(cookies[LOCATION_COOKIE_NAME] || null)

	// Check if new pricing tiers feature flag is enabled
	const newPricingTiersV2FF = useFeatureFlagVariantKey(FEATURE_FLAGS.NEW_PRICING_TIERS_V2)
	const limitedTrialFF = useFeatureFlagVariantKey(FEATURE_FLAGS.LIMITED_TRIAL)
	const isNewPricingEnabledV2 = newPricingTiersV2FF === 'test' || limitedTrialFF === 'test'

	// Fetch location data from IP API
	const fetchLocationData = async () => {
		if (locationData) return // Already have location data

		setIsLoading(true)
		setError(null)

		try {
			const response = await fetch('https://ipapi.co/json/?key=17mR0u0ikFdcKVtZxWyNH4Z3HC6ohD4RxKIZcLGP1vl41yV7wV')

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`)
			}

			const data: LocationData = await response.json()

			// Store in cookie with expiry
			const expiryDate = new Date()
			expiryDate.setDate(expiryDate.getDate() + LOCATION_COOKIE_EXPIRY_DAYS)

			setCookie(LOCATION_COOKIE_NAME, data, {
				expires: expiryDate,
				path: '/',
				sameSite: 'strict',
			})

			setLocationData(data)
		} catch (err) {
			console.error('Failed to fetch location data:', err)
			setError(err instanceof Error ? err.message : 'Failed to fetch location data')
		} finally {
			setIsLoading(false)
		}
	}

	// Fetch location on mount if not already available
	useEffect(() => {
		if (!locationData) {
			fetchLocationData()
		}
	}, [locationData])

	// Get pricing based on country code and feature flag
	const pricing = useMemo(() => {
		const pricingConfig = isNewPricingEnabledV2 ? NEW_COUNTRY_PRICING : COUNTRY_PRICING
		const defaultPricing = isNewPricingEnabledV2 ? NEW_DEFAULT_PRICING : DEFAULT_PRICING

		if (!locationData?.country_code) {
			return defaultPricing
		}

		return pricingConfig['US'] || defaultPricing
	}, [locationData, isNewPricingEnabledV2])

	return {
		pricing,
		locationData,
		isLoading,
		error,
		refetchLocation: fetchLocationData,
	}
}
